import React, { useContext, useEffect, useCallback } from 'react';
import {
	IEditSubcategoryRestaurantPropertiesContext,
	EditSubcategoryRestaurantPropertiesContext,
} from '../../../../editSubcategoryRestaurantPropertiesState/editSubcategoryRestaurantPropertiesContext';
import { Form } from 'antd';
import { preventDefaultForEnterKey } from 'utils/helpers';
import FullInfoField from 'components/FullInfoField/FullInfoField';
import { RestaurantSelect } from './components/RestaurantSelect';
import { ActivationScheduleInput } from './components/ActivationScheduleInput';
import { DeactivationScheduleInput } from './components/DeactivationScheduleInput';
import CronGenerator from 'components/CronGenerator/CronGenerator';
import { AvailableForRepetitionCheckbox } from './components/AvailableForRepetitionCheckbox';
import { IsHiddenCheckbox } from './components/IsHiddenCheckbox';
import { BtnBlock } from './components/BtnBlock';
import Text from 'components/Text/Text';
import { ItemList } from 'components/ViewPage//ItemList';
import { NoItemsNotification } from 'components/ViewPage/NoItemsNotification';
import style from './InputFields.module.scss';

export const InputFields: React.FC = () => {
	const {
		setIDRestaurant,
		changeActivationSchedule,
		changeDeactivationSchedule,
		toggleAvailableForRepetition,
		toggleIsHidden,
		setDataForSubmit,
		changeTypeOfModalWindow,
		subcategoryId,
		subcategoryName,
		customerName,
		categoryName,
		fetchedRestaurants,
		IDSelectedRestaurant,
		dishes,
		activationSchedule,
		deactivationSchedule,
		availableForRepetition,
		isHidden,
	} = useContext<IEditSubcategoryRestaurantPropertiesContext>(EditSubcategoryRestaurantPropertiesContext);
	const [form] = Form.useForm();

	useEffect(() => {
		form.setFieldsValue({
			IDSelectedRestaurant,
			activationSchedule,
			deactivationSchedule,
			availableForRepetition,
			isHidden,
		});
	}, [IDSelectedRestaurant, activationSchedule, deactivationSchedule, availableForRepetition, isHidden]);

	const submit = async () => {
		setDataForSubmit({
			subcategoryId: subcategoryId,
			restaurantId: IDSelectedRestaurant || '',
			activationCron: activationSchedule,
			deactivationCron: deactivationSchedule,
			availableForRepetition,
			isHidden,
		});
		changeTypeOfModalWindow('toConfirmSubmit');
	};

	const handleClickAssignDishes = useCallback(
		() => changeTypeOfModalWindow('assignDishes'),
		[changeTypeOfModalWindow],
	);

	const revalidateForm = form.validateFields;

	return (
		<div data-test="InputFields">
			<Form form={form} name="basic" onFinish={submit} onKeyPress={preventDefaultForEnterKey} layout="vertical">
				<Text
					className={style.name}
					translationKey={subcategoryName}
					size="xxl"
					weight="bold"
					display="block"
					withTooltip={true}
				/>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="restaurantPropertiesHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<div className={style.mainProps}>
						<FullInfoField label="customerLabel" value={customerName} useInForm={true} />
						<FullInfoField label="categoryLabel" value={categoryName} useInForm={true} />
					</div>
					<RestaurantSelect
						setIDRestaurant={setIDRestaurant}
						fetchedRestaurants={fetchedRestaurants}
						selectedRestaurantId={IDSelectedRestaurant}
					/>
					{IDSelectedRestaurant && !!dishes.length && (
						<>
							<Text
								className={style.dishLabel}
								translationNameSpace="form"
								translationKey="dishesLabel"
								theme="neutralLight"
								size="xs"
							/>
							<ItemList
								items={dishes}
								pathToItem="/menu-v2/dishes/edit?dishID="
								assignBtnTranslationKey="editDishListButton"
								onClickShowAssignModal={handleClickAssignDishes}
							/>
						</>
					)}
					{IDSelectedRestaurant && !dishes.length && (
						<NoItemsNotification
							title="noDishesAssigned"
							description="assignDishesToCustomersSubcategoryToBeAbleToEditThem"
							assignBtnTranslationKey="assignDishesButton"
							onClickShowAssignModal={handleClickAssignDishes}
						/>
					)}
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="scheduleHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<div className={style.row}>
						<ActivationScheduleInput changeActivationSchedule={changeActivationSchedule} />
						<DeactivationScheduleInput changeDeactivationSchedule={changeDeactivationSchedule} />
					</div>
					<CronGenerator />
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="availableForRepetitionHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<AvailableForRepetitionCheckbox
						toggleAvailableForRepetition={toggleAvailableForRepetition}
						availableForRepetition={availableForRepetition}
						isDisabled={!IDSelectedRestaurant}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="hideSubcategoryHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<IsHiddenCheckbox
						toggleIsHidden={toggleIsHidden}
						isHidden={isHidden}
						isDisabled={!IDSelectedRestaurant}
					/>
				</div>
				<BtnBlock revalidateForm={revalidateForm} />
			</Form>
		</div>
	);
};
